// Money Back — optional growth program layered on top of card economics.
//
// Defaults are zero. With all budgets / shares at zero, this module returns
// no costs, no sponsored value, and no funnel changes, preserving the baseline.

function n(value, fallback = 0) {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function clamp01(value) {
  return Math.max(0, Math.min(1, value));
}

function impactScale(rewardBudget, fullImpactMonthlyBudget) {
  const full = n(fullImpactMonthlyBudget);
  if (n(rewardBudget) <= 0 || full <= 0) return 0;
  return clamp01(rewardBudget / full);
}

function moneyBackConfig(inputs) {
  return inputs.drops?.moneyBack ?? null;
}

export function dropScenarioModifiers(inputs, scenario) {
  const moneyBack = moneyBackConfig(inputs);
  if (!moneyBack) return scenario;

  const configuredRewardBudget =
    n(moneyBack.pulsarMonthlyBudget) +
    n(moneyBack.yieldMonthlyCap) +
    n(moneyBack.proPaybackMonthlyBudget) +
    n(moneyBack.partnerSponsoredBudget);
  const scale = impactScale(configuredRewardBudget, moneyBack.fullImpactMonthlyBudget);

  return {
    ...scenario,
    cardActivationRate: clamp01(
      n(scenario.cardActivationRate) + scale * n(moneyBack.cardActivationLift),
    ),
    proConv: clamp01(n(scenario.proConv) + scale * n(moneyBack.proConversionLift)),
    churnFree: n(scenario.churnFree) * (1 - scale * clamp01(n(moneyBack.churnReduction))),
    churnPro: n(scenario.churnPro) * (1 - scale * clamp01(n(moneyBack.churnReduction))),
  };
}

export function computeDropPrograms(inputs, context) {
  const moneyBack = moneyBackConfig(inputs);
  if (!moneyBack) return emptyDropResult();

  const pulsarRewardValue = Math.max(0, n(moneyBack.pulsarMonthlyBudget));

  const yieldBudgetRaw =
    Math.max(0, n(context.stablecoinYieldRev)) * clamp01(n(moneyBack.yieldShare));
  const yieldRewardValue =
    n(moneyBack.yieldMonthlyCap) > 0 ? Math.min(yieldBudgetRaw, n(moneyBack.yieldMonthlyCap)) : 0;

  const eligibleProUsers = Math.max(
    0,
    n(context.proUsers) * clamp01(n(moneyBack.proPaybackEligibleShare)),
  );
  const proPaybackCapacity = eligibleProUsers * Math.max(0, n(moneyBack.proPaybackRebate));
  const proPaybackRewardValue = Math.min(
    Math.max(0, n(moneyBack.proPaybackMonthlyBudget)),
    proPaybackCapacity,
  );

  const partnerRewardValue = Math.max(0, n(moneyBack.partnerSponsoredBudget));
  const partnerPulsarCost = partnerRewardValue * clamp01(n(moneyBack.partnerCostShare));
  const partnerFundedValue = Math.max(0, partnerRewardValue - partnerPulsarCost);

  const userRewardValue =
    pulsarRewardValue + yieldRewardValue + proPaybackRewardValue + partnerRewardValue;
  const pulsarCost =
    pulsarRewardValue + yieldRewardValue + proPaybackRewardValue + partnerPulsarCost;

  const avgRewardAmount = Math.max(0, n(moneyBack.avgRewardAmount));
  const winners = avgRewardAmount > 0 ? userRewardValue / avgRewardAmount : 0;
  const generatedAppAccounts =
    (userRewardValue / 10) * Math.max(0, n(moneyBack.appAccountsPer10Reward));

  return {
    moneyBackCost: pulsarCost,
    moneyBackUserRewardValue: userRewardValue,
    moneyBackPartnerFundedValue: partnerFundedValue,
    moneyBackWinners: winners,
    moneyBackGeneratedAppAccounts: generatedAppAccounts,
  };
}

function emptyDropResult() {
  return {
    moneyBackCost: 0,
    moneyBackUserRewardValue: 0,
    moneyBackPartnerFundedValue: 0,
    moneyBackWinners: 0,
    moneyBackGeneratedAppAccounts: 0,
  };
}
